import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '@ohif/ui-next';
import VirtualizedDropdown, { type VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import {
  PEER_REVIEW_COLUMN_KEYS,
  PEER_REVIEW_COLUMN_LABELS,
  PEER_REVIEW_MODALITY_OPTIONS,
  type PeerReviewColumnKey,
} from '../../constants/peerReviewColumns';

export type PeerReviewToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  modality: string;
  onModalityChange: (value: string) => void;
  columnVisibility: Record<PeerReviewColumnKey, boolean>;
  onColumnVisibilityChange: (next: Record<PeerReviewColumnKey, boolean>) => void;
};

export default function PeerReviewToolbar({
  search,
  onSearchChange,
  modality,
  onModalityChange,
  columnVisibility,
  onColumnVisibilityChange,
}: Readonly<PeerReviewToolbarProps>) {
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!columnsOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [columnsOpen]);

  const modalityDropdownOptions: VirtualizedDropdownOption[] = useMemo(() => {
    const rows: VirtualizedDropdownOption[] = [{ id: 1, label: 'All modalities' }];
    PEER_REVIEW_MODALITY_OPTIONS.forEach((option, index) => {
      rows.push({ id: index + 2, label: option.label });
    });
    return rows;
  }, []);

  const modalityDropdownValue = useMemo(() => {
    if (!modality) {
      return 1;
    }
    const optionIndex = PEER_REVIEW_MODALITY_OPTIONS.findIndex(option => option.value === modality);
    return optionIndex >= 0 ? optionIndex + 2 : 1;
  }, [modality]);

  const handleModalityDropdownChange = (optionId: number | null) => {
    if (optionId == null || optionId === 1) {
      onModalityChange('');
      return;
    }
    const selected = PEER_REVIEW_MODALITY_OPTIONS[optionId - 2];
    if (selected) {
      onModalityChange(selected.value);
    }
  };

  return (
    <div className="peer-review-toolbar">
      <div className="peer-review-toolbar__left">
        <div className="peer-review-search">
          <Icons.Search
            width="16"
            height="16"
            className="peer-review-search__icon"
            aria-hidden
          />
          <input
            type="search"
            className="peer-review-search__input"
            placeholder="Search patient or order"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            aria-label="Search patient or order"
          />
        </div>

        <div className="peer-review-modality-filter">
          <VirtualizedDropdown
            ariaLabel="Filter by modality"
            value={modalityDropdownValue}
            onChange={handleModalityDropdownChange}
            options={modalityDropdownOptions}
            placeholder="All modalities"
            showNone={false}
            showSearch={false}
            mutedDisplayLabels={['All modalities']}
            containerClassName="peer-review-modality-vd-wrap w-full min-w-0"
            className="peer-review-modality-vd-trigger"
            variant="light"
            onlyKeyboardSyncsListScroll
          />
        </div>
      </div>

      <div
        className="peer-review-columns"
        ref={columnsRef}
      >
        <button
          type="button"
          className="peer-review-columns__trigger"
          aria-expanded={columnsOpen}
          aria-haspopup="menu"
          onClick={() => setColumnsOpen(open => !open)}
        >
          Columns
          <span
            className="peer-review-columns__caret"
            aria-hidden
          >
            ▾
          </span>
        </button>
        {columnsOpen ? (
          <div
            className="peer-review-columns__menu"
            role="menu"
          >
            {PEER_REVIEW_COLUMN_KEYS.map(key => (
              <label
                key={key}
                className="peer-review-columns__item"
                role="menuitemcheckbox"
                aria-checked={columnVisibility[key] !== false}
              >
                <input
                  type="checkbox"
                  checked={columnVisibility[key] !== false}
                  onChange={event => {
                    onColumnVisibilityChange({
                      ...columnVisibility,
                      [key]: event.target.checked,
                    });
                  }}
                />
                <span>{PEER_REVIEW_COLUMN_LABELS[key]}</span>
              </label>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
